import type { LocalizedString, SubmitQuizAttemptRequest } from "@repo/shared/types";
import { usePublicAnsweringSubjects } from "@/lib/hooks/use-public-flow/use-answering-subjects";
import {
  useApplyPublicPreviousAnswer,
  useDeclarePublicExperiences,
  usePublicProductAssessmentAssessments,
  usePublicProductAssessmentPreviousAnswer,
  usePublicProductAssessmentValue,
  usePublicProductTypesInUse,
  useStartPublicQuizAttempt,
  useSubmitPublicQuestionnaire,
  useSubmitPublicQuizAttempt,
} from "@/lib/hooks/use-public-flow/use-product-assessment";
import { useT } from "@/lib/i18n/use-t";
import { ProductAssessmentWidget } from "@/pages/clients/detail/flows/instance/product-assessment";
import { T } from "./product-assessment-renderer.i18n";
import type { ComponentStyleType } from "./types";

type PublicProductAssessmentRendererProps = {
  component: { config?: string };
  portfolioCollectionId: string | null;
  productTypes: Array<{ id: string; nameI18n: LocalizedString }>;
  isCompleted: boolean;
  hasValidationError?: boolean;
};

export function ProductAssessmentRenderer({
  component,
  portfolioCollectionId,
  productTypes,
  isCompleted,
  hasValidationError,
}: PublicProductAssessmentRendererProps) {
  const t = useT(T);
  const { inputData, calculatedData, isLoading: valueLoading } = usePublicProductAssessmentValue();
  const { previousAnswer, isLoading: prevLoading } = usePublicProductAssessmentPreviousAnswer();
  const { assessments, isLoading: assessmentsLoading } = usePublicProductAssessmentAssessments();
  const productTypeIdsInUse = usePublicProductTypesInUse();
  const {
    attribution,
    sessionSubjectId,
    isLoading: subjectsLoading,
  } = usePublicAnsweringSubjects();
  const declare = useDeclarePublicExperiences();
  const submitQuestionnaire = useSubmitPublicQuestionnaire();
  const startQuiz = useStartPublicQuizAttempt();
  const submitQuiz = useSubmitPublicQuizAttempt();
  const applyPrevious = useApplyPublicPreviousAnswer();

  if (!portfolioCollectionId || productTypes.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg">
        {t.productAssessmentNotConfigured}
      </div>
    );
  }

  let config: { style?: string; title?: string; description?: string } = {};
  try {
    config = component.config ? JSON.parse(component.config) : {};
  } catch {
    // Invalid config
  }

  // Reuse is applied server-side (same path as the advisor flow), so the merge carries
  // declarations, questionnaire answers and graded quiz states per product type.
  const handleApplyPrevious = async () => {
    await applyPrevious.mutateAsync();
  };

  const handleSubmitQuiz = (request: SubmitQuizAttemptRequest, subjectId?: string) =>
    submitQuiz.mutateAsync({ ...request, subjectId });

  // QL-505 — a group session (no `sessionSubjectId`) viewing a members-mode
  // group sees every member line read-only; only that member's own link
  // (`sessionSubjectId` set) may act on their own line.
  const isMembersMode = attribution?.kind === "members";
  const readOnlyMembers = isMembersMode && sessionSubjectId == null;

  // QL-505 — wait for the answering-subjects query too (like the advisor
  // wizard's `wizard-product-assessment.tsx`), so a members-mode client never
  // flashes as a single subject before `attribution`/`sessionSubjectId` load —
  // which would render every member's line as if it were one person's, then
  // snap to the real per-member view a moment later.
  if (valueLoading || prevLoading || assessmentsLoading || subjectsLoading) return null;

  return (
    <ProductAssessmentWidget
      portfolioCollectionId={portfolioCollectionId}
      portfolioCollectionName="Products"
      productTypes={productTypes}
      inputData={inputData}
      calculatedData={calculatedData}
      onDeclare={(declarations, subjectId) => declare.mutateAsync({ declarations, subjectId })}
      onSubmitQuestionnaire={(request, subjectId) =>
        submitQuestionnaire.mutateAsync({ ...request, subjectId })
      }
      disabled={isCompleted}
      assessments={assessments}
      productTypeIdsInUse={productTypeIdsInUse ?? undefined}
      onStartQuiz={(productTypeId, subjectId) =>
        startQuiz.mutateAsync({ productTypeId, subjectId })
      }
      onSubmitQuiz={handleSubmitQuiz}
      // QL-505 — reuse is per member: offered on a member link (the session's
      // own member) but never on a group session of a members-mode group.
      available={!!previousAnswer && !readOnlyMembers}
      sourceDate={previousAnswer?.timestamp ?? null}
      onApply={handleApplyPrevious}
      style={(config.style as ComponentStyleType) || "none"}
      title={config.title}
      description={config.description}
      hasValidationError={hasValidationError}
      attribution={attribution ?? undefined}
      sessionSubjectId={sessionSubjectId}
      readOnlyMembers={readOnlyMembers}
    />
  );
}
